import * as React from "react";
import Box from "@mui/material/Box";
import AddIcon from "@mui/icons-material/Add";
import { useSiteCommands } from "../../../queries/useSiteCommands.ts";

import {
  ClickAwayListener,
  Typography,
  Popover,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
} from "@mui/material";
import { useHistory } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import styled from "@emotion/styled";
import UpgradeIcon from "@mui/icons-material/Upgrade";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  H6HeadlineSmall,
  BodyLarge,
} from "../../../../components/StyledComponents/Typography/Typography.tsx";
import { StyledIconButton } from "../../../../components/StyledComponents/Buttons/AuradineButtons";
import { useForm } from "react-hook-form";
import useMinerPageContext from "../../../context/useMinerPageContext.js";
import useOrgSiteGroupTreeContext from "../../../context/useOrgSiteGroupTreeContext.js";
import useAppContextProvider from "../../../../AppContext/useAppContextProvider.js";
import MinerConfigurationDialog from "../../ConfigurationModal/ConfigurationModal.js";
import {
  CustomPowerForm,
  CustomRetuneForm,
  CustomTHSForm,
} from "../../CustomizationModal/CustomizationModal.js";
import UpgradeMinersDialog from "../../UpgradeMinersDialog/UpgradeMinersDialog.js";
import { useTheme } from "../../../../ThemeContext/ThemeContext.js";

const StyledPopoverContent = styled(Box)(({ colors }) => ({
  display: "flex",
  flexDirection: "column",
  borderRadius: "1rem",
  backgroundColor: colors?.background?.popover,
  boxShadow: "0px 2px 6px 2px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.3)",
}));

const StyledPopover = styled(Popover,)(({ colors }) => ({
  "& .MuiPaper-root": {
    borderRadius: "1rem", 
    backgroundColor: colors?.background?.popover,
  },
}));

const StyledHeaderAndFooterBox = styled(Box)`
  padding: 1.5rem;
`;

const MoreOptionsCard = (props) => {
  const {
    open,
    handleClose,
    anchorRef,
    downloadCSV,
    setTableKey,
    refetch,
  } = props;
  const { mutate: sendMinerCommands } = useSiteCommands();
  const {
    selectedSerials,
    setMinerConfigurationModalOpen,
    setWhichMinerConfigurationForm,
    setCustomizationModalOpen,
    setCustomPowerModalOpen,
    setRetuneModalOpen,
    setUpgradeModalOpen,
  } = useMinerPageContext();
  const { siteSelectedName } = useOrgSiteGroupTreeContext();
  const { setToastMessage, setToastOpen } =
    useAppContextProvider();
  const { colors } = useTheme();
  const { handleSubmit, control, watch } = useForm({
    defaultValues: {
      switchValue1: false,
      switchValue2: false,
      switchValue3: false,
      // Default value for the switch
    },
  });

  const menuOptions = [
    {
      title: "Download CSV",
      endIcon: () => (
        <span className="material-symbols-outlined">
          csv
        </span>
      ),
      onClick: () => {
        downloadCSV();
      },
    },
    {
      title: "Eco",
      endIcon: () => (
        <span className="material-symbols-outlined">
          eco
        </span>
      ),
      onClick: async () => {
        sendMinerCommands(
          {
            serials: selectedSerials,
            siteName: siteSelectedName,
            commands: {
              command: "fvmode",
              mode: "eco",
            },
            filter: {},
          },
          {
            onSuccess: async () => {
              setToastMessage(
                "Eco mode has been set for the selected miners. Please check the Job Results page or check the jobs column for each miner in the miner inventory tab"
              );
              setToastOpen(true);
              await refetch();
              setTableKey((prev) => prev + 1);
            },
            onError: (error) => {
              setToastMessage(
                `Error sending miner commands: ${error}`
              );
              setToastOpen(true);
            },
          }
        );

        handleClose();
      },
    },
    {
      title: "Restart miners",
      endIcon: () => (
        <span className="material-symbols-outlined">
          restart_alt
        </span>
      ),
      onClick: async () => {
        sendMinerCommands(
          {
            serials: selectedSerials,
            siteName: siteSelectedName,
            commands: {
              command: "restart",
            },
            filter: {},
          },
          {
            onSuccess: async () => {
              setToastMessage(
                "Restart miners has been triggered. Please check the Job Results page or check the jobs column for each miner in the miner inventory tab"
              );
              setToastOpen(true);
              await refetch();
              setTableKey((prev) => prev + 1);
            },
            onError: (error) => {
              setToastMessage(
                `Error sending miner commands: ${error.message}`
              );
              setToastOpen(true);
            },
          }
        );

        handleClose();
      },
    },
    {
      title: "Turbo",
      endIcon: () => (
        <span className="material-symbols-outlined">
          rocket_launch
        </span>
      ),
      onClick: async () => {
        sendMinerCommands(
          {
            serials: selectedSerials,
            siteName: siteSelectedName,
            commands: {
              command: "fvmode",
              mode: "turbo",
            },
            filter: {},
          },
          {
            onSuccess: async () => {
              setToastMessage(
                "Turbo mode has been set for the selected miners. Please check the Job Results page or check the jobs column for each miner in the miner inventory tab"
              );
              setToastOpen(true);
              await refetch();
              setTableKey((prev) => prev + 1);
            },
            onError: (error) => {
              setToastMessage(
                `Error sending miner commands: ${error.message}`
              );
              setToastOpen(true);
            },
          }
        );

        handleClose();
      },
      needDivider: true,
    },
    {
      title: "Fluxvision tuning",
      endIcon: () => (
        <span className="material-symbols-outlined">
          component_exchange
        </span>
      ),
      onClick: () => {
        setMinerConfigurationModalOpen(true);
        setWhichMinerConfigurationForm("fluxvision-tuning");
        handleClose();
      },
    },
    {
      title: "Flash LED",
      endIcon: () => (
        <span className="material-symbols-outlined">
          flare
        </span>
      ),
      onClick: () => {
        setMinerConfigurationModalOpen(true);
        setWhichMinerConfigurationForm("flashLED");
        handleClose();
      },
    },
    {
      title: "Optimize fan speed",
      endIcon: () => (
        <span className="material-symbols-outlined">
          mode_fan
        </span>
      ),
      onClick: () => {
        setMinerConfigurationModalOpen(true);
        setWhichMinerConfigurationForm("fanInStandby");
        handleClose();
      },
    },
    {
      title: "Enhanced ECO",
      endIcon: () => (
        <span className="material-symbols-outlined">
          energy_program_saving
        </span>
      ),
      onClick: () => {
        setMinerConfigurationModalOpen(true);
        setWhichMinerConfigurationForm("optimizeEco");
        handleClose();
      },
    },
    {
      title: "Miner W/O Internet",
      endIcon: () => (
        <span className="material-symbols-outlined">
          currency_bitcoin
        </span>
      ),
      onClick: () => {
        setMinerConfigurationModalOpen(true);
        setWhichMinerConfigurationForm(
          "mineWithoutInternet"
        );
        handleClose();
      },
    },
    {
      title: "Standby Mode",
      endIcon: () => (
        <span className="material-symbols-outlined">
          sleep
        </span>
      ),
      onClick: () => {
        setMinerConfigurationModalOpen(true);
        setWhichMinerConfigurationForm("standbyMode");
        handleClose();
      },
      needDivider: true,
    },
    {
      title: "Custom THs",
      endIcon: () => (
        <span className="material-symbols-outlined">
          tornado
        </span>
      ),
      onClick: () => {
        setCustomizationModalOpen(true);
        handleClose();
      },
    },
    {
      title: "Custom Power",
      endIcon: () => (
        <span className="material-symbols-outlined">
          bolt
        </span>
      ),
      onClick: () => {
        setCustomPowerModalOpen(true);
        handleClose();
      },
    },

    {
      title: "Retune",
      endIcon: () => (
        <span className="material-symbols-outlined">
          reset_settings
        </span>
      ),
      onClick: () => {
        setRetuneModalOpen(true);
        handleClose();
      },
      needDivider: true,
    },
    {
      title: "Upgrade Miners",
      endIcon: () => (
        <span className="material-symbols-outlined">
          system_update
        </span>
      ),
      onClick: () => {
        setUpgradeModalOpen(true);
        handleClose();
      },
    },
  ];

  return (
    <>
      <StyledPopover
        sx={{
          zIndex: 9999,
          borderRadius: "1rem",
        }}
        open={open}
        anchorEl={anchorRef.current}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        colors={colors}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <StyledPopoverContent
            colors={colors}
            display={"flex"}
            flexGrow={1}
          >
            <StyledHeaderAndFooterBox
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"flex-start"}
            >
              <H6HeadlineSmall>
                Additional actions
              </H6HeadlineSmall>
            </StyledHeaderAndFooterBox>

            <Box
              display={"flex"}
              flexDirection={"column"}
              pb={"1rem"}
            >
              <Box
                display={"flex"}
                maxHeight={"20rem"}
                overflow={"auto"}
                flexDirection={"column"}
                sx={{
                  scrollbarWidth: "thin",
                  scrollBehavior: "smooth ",
                  willChange: "transform",
                }}
              >
                {menuOptions.map((option, index) => (
                  <>
                    <ListItemButton
                      key={index}
                      onClick={option.onClick}
                      sx={{
                        borderRadius: "1rem",
                        padding: "0.5rem 1rem",
                        margin: "0.5rem 0",
                        backgroundColor: colors?.background?.popover,
                        "&:hover": {
                          backgroundColor: colors.hover.muted,
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <BodyLarge>
                            {option.title}
                          </BodyLarge>
                        }
                      />
                      <ListItemIcon
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          color: colors.text.primary,
                        }}
                      >
                        {option.endIcon()}
                      </ListItemIcon>
                    </ListItemButton>
                    {option.needDivider && <Divider />}
                  </>
                ))}
              </Box>
            </Box>
          </StyledPopoverContent>
        </ClickAwayListener>
      </StyledPopover>
    </>
  );
};

export const MinerOperationsTab = (props) => {
  const { downloadCSV, setTableKey, refetch } = props;
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef(null);
  const { user } = useAuth0();
  const { selectedSerials } = useMinerPageContext();
  const { siteSelectedName, groupSelectedName } =
    useOrgSiteGroupTreeContext();
  const isDisabled =
    selectedSerials.length === 0 ||
    !(
      siteSelectedName !== "" ||
      (siteSelectedName !== "" && groupSelectedName !== "")
    );
  const { colors } = useTheme();

  const navigate = useHistory();
  const handleClick = (event) => {
    event.stopPropagation();
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = () => {
    setOpen(false);
  };
  return (
    <React.Fragment>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <StyledIconButton 
          colors={colors}
          ref={anchorRef}
          disabled={isDisabled}
          onClick={(event) => handleClick(event)}
        >
          <MoreVertIcon
            fontSize={"medium"}
            sx={{
              color: isDisabled
                ? "rgba(0, 0, 0, 0.26) !important"
                : "#000",
            }}
          />
        </StyledIconButton>
      </Box>

      <MoreOptionsCard
        open={open}
        handleClose={handleClose}
        anchorRef={anchorRef}
        downloadCSV={downloadCSV}
        setTableKey={setTableKey}
        refetch={refetch}
      />
      <MinerConfigurationDialog />
      <CustomTHSForm />
      <CustomPowerForm />
      <CustomRetuneForm />
      <UpgradeMinersDialog />
    </React.Fragment>
  );
};
